import React, { Component, forwardRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation, withTranslation } from 'react-i18next';
import { RostersTabs } from 'features/rosters/index';
import {
  MultiRadio,
  SimpleModal,
  Input,
  Pagination,
  Spinner,
  AlertNotice,
  NoResults,
} from 'shared/components/index';
import { RostersList } from 'features/rosters';
import { ASSIGNED_STATUS, EXAM_CATEGORIES, PROGRAM_PARENT_CODES } from 'shared/constants';
import { ASSIGN_FILTERS, toRadioList } from '../helpers';
import { ValidationService } from 'shared/services';
import { getFirstElement } from 'shared/helpers';
import FiltersIcon from 'assets/svgs/filterIconBlue.svg';
import SearchIcon from 'assets/svgs/searchIcon.svg';
import { omit, uniqueId, isEqual, isEmpty, uniqBy } from 'lodash';

import './RostersPanel.scss';
let initialColumns = [
  {
    Header: 'HS Code',
    key: 'hsCode',
    label: 'HS Code',
    checked: true,
  },
  {
    Header: 'Match ID',
    key: 'matchID',
    label: 'Match ID',
    checked: true,
  },
  {
    Header: 'Exam type',
    key: 'examType',
    label: 'Exam type',
    checked: true,
  },
  {
    Header: 'Timing',
    key: 'timing',
    label: 'Timing',
    checked: true,
  },
  {
    Header: 'Room assigned',
    key: 'roomAssigned',
    label: 'Room assigned',
    checked: true,
  },
];
const byodColumn = {
  Header: 'BYOD',
  key: 'byod',
  label: 'BYOD',
  checked: true,
};
const testDateColumn = {
  Header: 'Test Date',
  key: 'testDate',
  label: 'Test Date',
  checked: true,
};

const byodList = [
  {
    key: 'all',
    label: 'All',
    value: '',
  },
  {
    key: 'yes',
    label: 'Yes',
    value: 'true',
  },
  {
    key: 'no',
    label: 'No',
    value: 'false',
  },
];

const initialFilters = [
  { value: 'denied', id: 'Denied', label: `Denied`, checked: false },
  { value: 'absent', id: 'Absent', label: `Absent`, checked: false },
];

class RostersPanel extends Component {
  constructor(props) {
    super(props);
    if (props.forwardedRef) {
      props.forwardedRef.current = this;
    }

    this.state = {
      examineeTypesList: [],
      assignedList: toRadioList(ASSIGNED_STATUS, ASSIGN_FILTERS),
      datesList: [],
      selectedExamineeType: '',
      selectedAssigned: '',
      selectedBYOD: '',
      selectedDate: '',
      selectedExamCategory: props.locationExamineeCount.length
        ? getFirstElement(props.locationExamineeCount).type
        : '',
      // showFilters: true,
      searchValue: '',
      warningModalOpen: false,
      errorAlphaNumeric: '',
      headerColumns: [testDateColumn, ...initialColumns, byodColumn],
      filters: initialFilters,
    };

    this.onTypeChange = this.onTypeChange.bind(this);
    this.onAssignChange = this.onAssignChange.bind(this);
    this.onExamCategoryChange = this.onExamCategoryChange.bind(this);
    this.clearFilters = this.clearFilters.bind(this);
    this.onResetExamCategory = this.onResetExamCategory.bind(this);
    this.toggleWarningModal = this.toggleWarningModal.bind(this);
    this.onAcceptChangeExamCategory = this.onAcceptChangeExamCategory.bind(this);
    this.clearSearch = this.clearSearch.bind(this);
    this.onChangeSearchValue = this.onChangeSearchValue.bind(this);
    this.onSearchByExaminee = this.onSearchByExaminee.bind(this);
    this.toggleFilters = this.toggleFilters.bind(this);
    this.clearRadioFilters = this.clearRadioFilters.bind(this);
    this.onBYODChange = this.onBYODChange.bind(this);
    this.onDateChange = this.onDateChange.bind(this);
    this.setSearchValues = this.setSearchValues.bind(this);
    this.setNewHeaders = this.setNewHeaders.bind(this);
    this.onSelectAll = this.onSelectAll.bind(this);
    this.setExamineeTypesList = this.setExamineeTypesList.bind(this);
  }

  componentDidMount() {
    if (this.props.dateAvailable) {
      initialColumns = [testDateColumn, ...initialColumns];
    }
    if (this.props.cycle.isByodAllowed) {
      initialColumns = [...initialColumns, byodColumn];
    }
    this.setExamineeTypesList();
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevProps.deniedCount !== this.props.deniedCount ||
      prevProps.absentCount !== this.props.absentCount
    ) {
      const status = {
        absent: 'absentCount',
        denied: 'deniedCount',
      };
      const filters = this.state.filters.map((item) => ({
        ...item,
        label: `${item.id} (${this.props[status[item.value]]})`,
      }));
      this.setState({
        filters,
      });
    }
    const { dateFilters, locationExamineeCount, hasRescheduleDate } = this.props;
    if (
      hasRescheduleDate &&
      !isEmpty(dateFilters) &&
      (!isEqual(dateFilters, prevProps.dateFilters) || !this.state.datesList.length)
    ) {
      this.setState({
        datesList: toRadioList(dateFilters),
      });
    }
    if (!this.state.selectedExamCategory && locationExamineeCount.length) {
      this.setState({
        selectedExamCategory: getFirstElement(locationExamineeCount).type,
      });
    }
    if (
      this.props.regTypes !== prevProps.regTypes ||
      this.state.selectedExamCategory !== prevState.selectedExamCategory
    ) {
      this.setExamineeTypesList();
    }
  }

  setNewHeaders(newHeaders) {
    // If the user is updating columns, but they aren't in this table (e.g., BYOD column for a paper regType), we need to preserve the existing value
    const headerColumns = this.state.headerColumns.map((col) => {
      const newCol = newHeaders.find(({ key }) => key === col.key);
      return newCol || col;
    });
    this.setState({
      headerColumns,
    });
    globalThis.sessionStorage.setItem(
      `${this.state.selectedExamCategory}-columns`,
      JSON.stringify(headerColumns)
    );
  }

  setExamineeTypesList() {
    const examineeTypesList = this.props.regTypes.map((item) => ({
      key: `key-${item.name}`,
      label: item.name,
      value: item.id,
    }));

    this.setState({
      examineeTypesList: [{ key: '1', label: 'All', value: '' }, ...examineeTypesList],
    });
  }

  clearFilters(defaultFlag) {
    const { locationExamineeCount, setTemporarySearchValue } = this.props;
    const filters = this.state.filters.filter((item) => {
      item.checked = false;
      return item;
    });
    this.setState({
      selectedExamineeType: '',
      selectedAssigned: '',
      selectedDate: '',
      selectedExamCategory: defaultFlag
        ? getFirstElement(locationExamineeCount).type
        : this.props.filterParams.category,
      searchValue: '',
      filters,
    });
    setTemporarySearchValue('');
    this.props.setHasSearchValue(false);
  }

  onTypeChange(e) {
    const { value } = e.target;
    const { searchValue } = this.state;
    const isNCRC = this.props.regTypes.find((regType) => regType.id == value)?.code === 'NCRC';

    this.setState({
      selectedExamineeType: value,
    });

    if (!this.state.searchValue) {
      this.props.setTemporarySearchValue('');
    }

    // Clear selected examinees when filter changes (WorkKeys only)
    if (this.props.program?.parentCode?.toLowerCase() === PROGRAM_PARENT_CODES.workKeys) {
      this.props.onDeselectAllExaminees();
    }

    // Remove both properties first, then add only the one we need
    const { regTypeId, ncrc, ...restFilters } = this.props.filterParams;

    this.props.onUpdateFilters({
      ...restFilters,
      ...(isNCRC ? { ncrc: true } : { regTypeId: value }),
      name: searchValue,
    });
  }

  onAssignChange(e) {
    const { value } = e.target;

    this.setState({
      selectedAssigned: value,
    });
    if (!this.state.searchValue) {
      this.props.setTemporarySearchValue('');
    }

    this.props.onUpdateFilters({
      ...this.props.filterParams,
      assigned: value,
      name: this.state.searchValue,
    });
  }

  onBYODChange(e) {
    const { value } = e.target;

    this.setState({
      selectedBYOD: value,
    });

    this.props.onUpdateFilters({
      ...this.props.filterParams,
      byod: value,
      name: this.state.searchValue,
    });
  }

  onDateChange(e) {
    const { value } = e.target;

    this.setState({
      selectedDate: value,
    });

    this.props.onUpdateFilters({
      ...this.props.filterParams,
      date: value,
      name: this.state.searchValue,
    });
  }

  onChangeAbsentDenied = (e) => {
    const { value } = e.target;
    const filters = this.state.filters.filter((item) => {
      if (item.id === value) {
        item.checked = !item.checked;
      }
      return item;
    });
    this.setState({
      filters,
    });
    const selected = filters.find((item) => item.id === value);

    this.props.onUpdateFilters({
      ...this.props.filterParams,
      [selected.value]: selected?.checked ? 1 : 0,
      name: this.state.searchValue,
    });
  };

  onExamCategoryChange(value) {
    const { examineesSelectedFlag } = this.props;
    const { selectedExamCategory, searchValue, selectedDate } = this.state;

    this.setState((prevState) => ({
      prevSelectedExamCategory: prevState.selectedExamCategory,
      selectedExamCategory: value,
    }));

    if (selectedExamCategory !== value) {
      if (examineesSelectedFlag) {
        this.setState({
          warningCategoryOpen: true,
        });
        this.toggleWarningModal();
        return;
      }
    }

    if (value === EXAM_CATEGORIES.onlineSectionRetest) {
      this.setState({
        selectedExamineeType: '',
      });
    }

    this.props.onUpdateFilters({
      ...this.props.filterParams,
      category: value,
      name: searchValue,
      date: selectedDate,
      regTypeId:
        value === EXAM_CATEGORIES.onlineSectionRetest ? '' : this.props.filterParams.regTypeId,
      denied: this.state.filters.find((item) => item.value === 'denied').checked ? 1 : 0,
      absent: this.state.filters.find((item) => item.value === 'absent').checked ? 1 : 0,
    });
  }

  onSearchByExaminee(e) {
    if (this.props.temporarySearchValue && (e.key === 'Enter' || e.type === 'click')) {
      this.setState({
        searchValue: this.props.temporarySearchValue,
      });
      this.props.setHasSearchValue(true);
      this.props.onUpdateFilters({
        ...this.props.filterParams,
        name: this.props.temporarySearchValue,
      });
    }
  }

  onChangeSearchValue(e) {
    const { value } = e.target;

    if (value && value.length > 30) {
      return;
    }
    if (value && !ValidationService.rosterSearchValidation(value)) {
      this.setState({
        errorAlphaNumeric: 'errors.alphanumeric',
      });
      return;
    }

    this.setState({
      errorAlphaNumeric: '',
    });

    this.props.setTemporarySearchValue(value);

    if (!value) {
      this.props.onUpdateFilters({
        ...omit(this.props.filterParams, 'name'),
      });
    }
  }

  onResetExamCategory() {
    this.setState({
      selectedExamCategory: this.state.prevSelectedExamCategory,
    });
  }

  toggleWarningModal() {
    if (this.state.warningModalOpen) {
      this.onResetExamCategory();
    }
    this.setState((prevState) => ({
      warningModalOpen: !prevState.warningModalOpen,
    }));
  }

  onAcceptChangeExamCategory() {
    const { selectedExamCategory } = this.state;

    if (selectedExamCategory === EXAM_CATEGORIES.onlineSectionRetest) {
      this.setState({
        selectedExamineeType: '',
      });
    }
    this.setState({
      warningModalOpen: false,
    });
    console.log(this.props);
    this.props.onDeselectAllExaminees();
    this.props.onUpdateFilters({
      ...this.props.filterParams,
      category: this.state.selectedExamCategory,
      name: this.state.searchValue,
      date: this.state.selectedDate,
      regTypeId:
        selectedExamCategory === EXAM_CATEGORIES.onlineSectionRetest
          ? ''
          : this.props.filterParams.writing,
    });
  }

  clearSearch() {
    this.props.onUpdateFilters({
      ...omit(this.props.filterParams, 'name'),
    });
    this.setState(() => ({
      searchValue: '',
      errorAlphaNumeric: '',
    }));
    this.props.setTemporarySearchValue('');
    this.props.setHasSearchValue(false);
  }

  toggleFilters() {
    // this.setState(prevState=>({
    //   showFilters: !prevState.showFilters,
    // }))
    this.props.toggleFilters();
  }

  clearRadioFilters() {
    const filters = this.state.filters.filter((item) => {
      item.checked = false;
      return item;
    });
    this.setState({
      selectedExamineeType: '',
      selectedAssigned: '',
      selectedDate: '',
      filters,
    });

    this.props.onUpdateFilters({
      ...this.props.filterParams,
      name: this.state.searchValue,
      assigned: '',
      regTypeId: '',
      date: '',
      denied: 0,
      absent: 0,
    });
  }

  setSearchValues(searchValue, temporarySearchValue) {
    this.setState({
      searchValue: searchValue,
    });
    this.props.setTemporarySearchValue(temporarySearchValue);
  }

  onSelectAll(e) {
    const examineesList = this.props.locationEventExaminees;
    const { checked } = e.target;
    let list = this.props.listOfSelectedExaminees.concat([]);
    if (checked || e.target.innerText === this.props.t('common.labels.selectAll')) {
      examineesList.map((examinee) => {
        examinee.checked = checked;
        list.push(examinee);
      });
    } else {
      examineesList.map((examinee) => {
        examinee.checked = checked;
        list.splice(
          list.findIndex((el) => el.id === examinee.id),
          1
        );
      });
    }

    this.props.onUpdateSelectedExaminees(uniqBy(list, 'id'));
  }

  render() {
    const {
      selectedExamCategory,
      examineeTypesList,
      assignedList,
      warningModalOpen,
      errorAlphaNumeric,
      selectedExamineeType,
      selectedAssigned,
      selectedBYOD,
      datesList,
      selectedDate,
    } = this.state;
    const {
      cycle,
      locationExamineeCount,
      dateFilters,
      hasRescheduleDate,
      showFilters,
      itemsTotalCount,
      onChangePage,
      paginationDataParams,
      pageSize,
      pageSizeOptions,
      ofLabel,
      hasSearchValue,
      setTemporarySearchValue,
      temporarySearchValue,
      onReloadExaminees,
      selectedLocation,
      onDeselectAllExaminees,
      listOfSelectedExaminees,
      goBack,
      showSelectPage,
      loadingExaminees,
      locationEventExaminees,
      singleRoom,
      onUpdateSelectedExaminees,
      dateAvailable,
      clearFilters,
      hasNowManagedFlag,
      openDeniedModal,
      openAbsentModal,
    } = this.props;
    const hasChecked = this.state.filters.find((item) => item.checked);
    const isBYODVisible =
      cycle?.isByodAllowed &&
      (locationExamineeCount || []).find((o = {}) => o.type === selectedExamCategory)?.onlineFlag;
    return (
      <div className="rosters-filters-section">
        <RostersTabs
          locationExamineeCount={locationExamineeCount}
          selectedExamCategory={selectedExamCategory}
          onExamCategoryChange={this.onExamCategoryChange}
        />
        <div className="search-container">
          <SearchBox
            toggleFilters={this.toggleFilters}
            showFilters={showFilters}
            className=""
            searchValue={this.props.temporarySearchValue}
            onChange={this.onChangeSearchValue}
            onSearch={this.onSearchByExaminee}
            onKeyPress={this.onSearchByExaminee}
            clearSearch={this.clearSearch}
            errors={errorAlphaNumeric}
          />
        </div>
        <Pagination
          itemsTotalCount={itemsTotalCount}
          onChangePage={onChangePage}
          paginationDataParams={paginationDataParams}
          pageSize={pageSize}
          pageSizeOptions={pageSizeOptions}
          ofLabel={ofLabel}
          showSelectPage={showSelectPage}
          setTemporarySearchValue={setTemporarySearchValue}
          temporarySearchValue={temporarySearchValue}
          hasSearchValue={hasSearchValue}
          onReloadExaminees={onReloadExaminees}
          selectedLocation={selectedLocation}
          onDeselectAllExaminees={onDeselectAllExaminees}
          listOfSelectedExaminees={listOfSelectedExaminees}
          goBack={goBack}
          initialColumns={initialColumns.filter((col) => col.key !== 'byod' || isBYODVisible)}
          headerColumns={this.state.headerColumns.filter(
            (col) => col.key !== 'byod' || isBYODVisible
          )}
          setNewHeaders={this.setNewHeaders}
          selectedExamCategory={selectedExamCategory}
          onSelectAll={this.onSelectAll}
          openAbsentModal={openAbsentModal}
          openDeniedModal={openDeniedModal}
        />
        {this.props.rostersTopFilters}
        <div className="left-panel">
          <div
            className={`radio-filters scrollable-content ${
              showFilters ? '' : 'hide-desktop-filters'
            }`}>
            <div className="filters-header">
              <FiltersIcon className="filter-icon" />
              <div className="fw-bold filters-title">{this.props.t('rosters.filtersTitle')}</div>
              {(selectedExamineeType ||
                selectedAssigned ||
                selectedDate ||
                !isEmpty(hasChecked)) && (
                <button
                  className="btn-link fw-bold d-inline-block"
                  onClick={this.clearRadioFilters}>
                  {this.props.t('common.labels.clear')}
                </button>
              )}
              <button
                className="btn-link fw-bold d-inline-block show-desktop"
                onClick={this.toggleFilters}>
                {this.props.t('common.labels.hide')}
              </button>
            </div>
            <MultiRadio
              title={this.props.t('rosters.examTypeFilterLabel')}
              onChange={this.onTypeChange}
              radioList={examineeTypesList}
              checkedValue={selectedExamineeType}
              disabledList={selectedExamCategory === EXAM_CATEGORIES.onlineSectionRetest}
              name="examineeTypeList"
            />
            <MultiRadio
              title={this.props.t('rosters.assignmentFilterLabel')}
              onChange={this.onAssignChange}
              radioList={assignedList}
              checkedValue={selectedAssigned}
              name="assignedList"
            />
            {!isEmpty(dateFilters) && hasRescheduleDate && (
              <MultiRadio
                title={this.props.t('rosters.testDateFilterLabel')}
                onChange={this.onDateChange}
                radioList={datesList}
                checkedValue={selectedDate}
                name="assignedList"
              />
            )}
            {isBYODVisible && (
              <MultiRadio
                title={this.props.t('rosters.byodFilterLabel')}
                onChange={this.onBYODChange}
                radioList={byodList}
                checkedValue={selectedBYOD}
                name="byodList"
              />
            )}
            <div className="absent-denied-container">
              <h6 className="title">{this.props.t('rosters.absentDeniedTitle')}</h6>
              {this.state.filters.map((item) => (
                <div key={item.id}>
                  <div>
                    <input
                      type="checkbox"
                      name={`roster-${item.value}`}
                      id={item.id}
                      value={item.id}
                      onChange={this.onChangeAbsentDenied}
                      checked={item.checked}
                    />
                    <label htmlFor={item.id}>{this.props.t(item.label)}</label>
                  </div>
                  <div>{item.icon}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="right-panel">
          {this.props.filterParams.category === EXAM_CATEGORIES.standBy && (
            <AlertNotice message={this.props.t('rosters.standByInfoMessage')} />
          )}
          <div className={`roster-content ${showFilters ? 'fit-content' : ''}`}>
            {loadingExaminees ? (
              <Spinner />
            ) : (
              <>
                {!!locationEventExaminees?.length && (
                  <RostersList
                    locationEventExaminees={locationEventExaminees}
                    singleRoom={singleRoom}
                    onUpdateSelectedExaminees={onUpdateSelectedExaminees}
                    listOfSelectedExaminees={listOfSelectedExaminees}
                    dateAvailable={dateAvailable}
                    visibleColumns={this.state.headerColumns.filter(
                      (col) => col.key !== 'byod' || isBYODVisible
                    )}
                    onSelectAll={this.onSelectAll}
                    hasNowManagedFlag={hasNowManagedFlag}
                  />
                )}
                {!itemsTotalCount && <NoResults noResultsClear={clearFilters} />}
              </>
            )}
          </div>
        </div>
        <SimpleModal
          isOpen={warningModalOpen}
          onCancel={this.toggleWarningModal}
          onConfirm={this.onAcceptChangeExamCategory}
          confirmLabel={this.props.t('common.labels.continue')}
          cancelLabel={this.props.t('common.labels.back')}
          headline={this.props.t('rosters.uncheckWarningMessage')}
        />
      </div>
    );
  }
}

const TranslatedRostersPanel = withTranslation()(RostersPanel);
const RostersPanelWithRef = forwardRef((props, ref) => (
  <TranslatedRostersPanel {...props} forwardedRef={ref} />
));

export default RostersPanelWithRef;
RostersPanel.propTypes = {
  cycle: PropTypes.shape({
    isByodAllowed: PropTypes.bool,
  }).isRequired,
  locationExamineeCount: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      onlineFlag: PropTypes.bool,
    })
  ).isRequired,
  dateFilters: PropTypes.arrayOf(PropTypes.string).isRequired,
  hasRescheduleDate: PropTypes.bool,
  rostersTopFilters: PropTypes.element,
  itemsTotalCount: PropTypes.number,
  onChangePage: PropTypes.func.isRequired,
  paginationDataParams: PropTypes.object.isRequired,
  pageSize: PropTypes.number.isRequired,
  pageSizeOptions: PropTypes.arrayOf(PropTypes.number).isRequired,
  ofLabel: PropTypes.string,
  hasSearchValue: PropTypes.bool.isRequired,
  setTemporarySearchValue: PropTypes.func.isRequired,
  temporarySearchValue: PropTypes.string.isRequired,
  onReloadExaminees: PropTypes.func.isRequired,
  selectedLocation: PropTypes.object.isRequired,
  onDeselectAllExaminees: PropTypes.func.isRequired,
  listOfSelectedExaminees: PropTypes.arrayOf(PropTypes.object).isRequired,
  goBack: PropTypes.func.isRequired,
  showSelectPage: PropTypes.bool,
  showFilters: PropTypes.bool,
  loadingExaminees: PropTypes.bool,
  locationEventExaminees: PropTypes.arrayOf(PropTypes.object).isRequired,
  singleRoom: PropTypes.bool,
  onUpdateSelectedExaminees: PropTypes.func.isRequired,
  dateAvailable: PropTypes.bool,
  filterParams: PropTypes.shape({
    category: PropTypes.string,
    ncrc: PropTypes.string,
    regTypeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    writing: PropTypes.string,
  }).isRequired,
  onUpdateFilters: PropTypes.func.isRequired,
  regTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      code: PropTypes.string,
    })
  ).isRequired,
  examineesSelectedFlag: PropTypes.bool.isRequired,
  program: PropTypes.shape({
    parentCode: PropTypes.string,
  }),
  clearFilters: PropTypes.func.isRequired,
  hasNowManagedFlag: PropTypes.bool,
  deniedCount: PropTypes.number,
  absentCount: PropTypes.number,
  toggleFilters: PropTypes.func.isRequired,
  openDeniedModal: PropTypes.func.isRequired,
  openAbsentModal: PropTypes.func.isRequired,
  setHasSearchValue: PropTypes.func.isRequired,
  forwardedRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  t: PropTypes.func.isRequired,
};

const SearchBox = (props) => {
  const { t } = useTranslation();
  return (
    <div
      className={`search-box ${props.className} ${
        props.searchAvailable ? 'input-displayed' : 'btn-displayed'
      }`}>
      {
        <div className="input-search-container">
          {!props.showFilters && (
            <>
              <div className="filter-button show-desktop" onClick={props.toggleFilters}>
                <FiltersIcon /> {t('rosters.showFilters')}
              </div>
              <div className="vertical-separator"></div>
            </>
          )}
          <span className="search-label">Search by</span>
          <Input
            autocomplete="false"
            type="text"
            name="searchInput"
            id={uniqueId('search-input')}
            value={props.searchValue}
            placeholder={t('rosters.searchPlaceholder')}
            onChange={props.onChange}
            onKeyPress={props.onKeyPress}
            errors={props.errors}
            clearValue={props.clearSearch}
          />
          <button className="btn-search" onClick={props.onSearch}>
            <SearchIcon />
          </button>
        </div>
      }
    </div>
  );
};

SearchBox.propTypes = {
  className: PropTypes.string,
  searchAvailable: PropTypes.bool,
  showFilters: PropTypes.bool,
  toggleFilters: PropTypes.func,
  searchValue: PropTypes.string,
  onChange: PropTypes.func,
  onKeyPress: PropTypes.func,
  onSearch: PropTypes.func,
  clearSearch: PropTypes.func,
  errors: PropTypes.string,
};
